import type { OutcomesApi } from "@/v2/api/metadata/outcomes";
import type { BackofficeContext } from "@/v2/framework/context/types";
import type { ColumnEntry } from "@/v2/framework/column/types";
import { ColumnIconType, FieldType } from "@/v2/framework/column/types";
import type {
  ContentTab,
  DialogConfig,
  DialogTabSaveContext,
  GridTab,
  GridTabSaveContext,
  TranslationsTab,
} from "@/v2/framework/dialog/types";
import type { GridViewBlock } from "@/v2/framework/view/types";
import { readApiData } from "@/v2/api/response";
import { formatTranslationsForUpdate } from "@/services/helpers";
import { t } from "@/services/i18n";
import {
  addFeedMappingRow,
  buildFeedMappingGrid,
  createFeedMappingSubmitGate,
  fetchFeedTypeMappingsList,
} from "@/v2/kits/feed-mapping";
import { createInheritableTabSave } from "@/v2/kits/tenant-inheritance";
import { notify, notifyApiError, notifyApiSuccess } from "@/v2/notifications";
import { NXGDSStatusVariant } from "@nsftx/seven-nxg-design-system-backoffice";
import { marketGamesStatusOptions, marketStatusOptions } from "../../../shared";
import { buildMarketTranslationFields } from "../translation-fields";

function outcomeId(values: Record<string, unknown>): string | number {
  return values.id as string | number;
}

function notifySuccess(): void {
  notifyApiSuccess("Successfully updated.");
}

const outcomeColumns: ColumnEntry[] = [
  { key: "id", defaultVisibility: true, title: "table_columns.id", fieldType: FieldType.TextCell },
  {
    key: "name",
    defaultVisibility: true,
    title: "table_columns.name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "eventMarketOutcomeName",
    defaultVisibility: false,
    title: "table_columns.event_market_outcome_name",
    fieldType: FieldType.InputCell,
  },
  {
    key: "position",
    defaultVisibility: false,
    title: "table_columns.position",
    fieldType: FieldType.InputCell,
  },
  {
    key: "status",
    defaultVisibility: true,
    title: "table_columns.status",
    fieldType: FieldType.TextCell,
    valueFormatter: (row: unknown) => {
      const status = marketStatusOptions.find(
        (option) => option.value === (row as { status?: number }).status,
      );
      return t(status?.title ?? "");
    },
    icon: {
      type: ColumnIconType.Prepend,
      formatter: (row: unknown) => {
        const status = marketStatusOptions.find(
          (option) => option.value === (row as { status?: number }).status,
        );
        return { code: status?.icon?.code ?? "", color: status?.icon?.color ?? "" };
      },
    },
  },
  {
    key: "gridChrome",
    defaultVisibility: true,
    configurableVisibility: false,
    title: "",
    fieldType: FieldType.ActionsCell,
    sticky: true,
    rowActions: [
      { icon: "edit-ng", label: "Edit", onClick: (ctx) => ctx.openDialog("edit") },
      { icon: "trash-ng", label: "Delete", onClick: (ctx) => ctx.openDialog("delete") },
    ],
  },
];

function gameGroup(name: "prematchGame" | "liveGame"): ContentTab["children"] {
  return [
    {
      type: "group",
      name,
      label: "",
      children: [
        {
          type: "select-field",
          name: "status",
          label: "Status",
          required: true,
          options: marketGamesStatusOptions,
          inheritance: { computedKey: "computedStatus", parentKey: "parentStatus" },
        },
        {
          type: "text-field",
          name: "position",
          label: "Position",
          inheritance: { computedKey: "computedPosition", parentKey: "parentPosition" },
        },
      ],
    },
  ];
}

function createOutcomeDialogs(options: {
  ctx: BackofficeContext;
  outcomesApi: OutcomesApi;
  getMarketId: () => unknown;
}): GridViewBlock["dialogs"] {
  const { ctx, outcomesApi, getMarketId } = options;

  const mainChildren: ContentTab["children"] = [
    {
      type: "text-field",
      name: "name",
      label: "Name",
      placeholder: "Enter name",
      inheritance: { computedKey: "computedName", parentKey: "parentName" },
    },
    {
      type: "text-field",
      name: "eventMarketOutcomeName",
      label: "Event market outcome name",
      inheritance: {
        computedKey: "computedEventMarketOutcomeName",
        parentKey: "parentEventMarketOutcomeName",
      },
    },
    {
      type: "select-field",
      name: "status",
      label: "Status",
      required: true,
      options: marketStatusOptions,
      inheritance: { computedKey: "computedStatus", parentKey: "parentStatus" },
    },
    {
      type: "text-field",
      name: "info",
      label: "Info",
      inheritance: { computedKey: "computedInfo", parentKey: "parentInfo" },
    },
    {
      type: "text-field",
      name: "position",
      label: "Position",
      inheritance: { computedKey: "computedPosition", parentKey: "parentPosition" },
    },
  ];

  const translationsTab: TranslationsTab = {
    kind: "translations",
    id: "translations",
    label: "Translations",
    persistence: "isolated",
    parentSubmitWithUnsavedTranslations: "block",
    fields: buildMarketTranslationFields,
    api: {
      get: async ({ values }) => {
        const translations = readApiData(await outcomesApi.getTranslations(outcomeId(values)));
        const list = Array.isArray(translations) ? translations : [];
        const translationsPerLanguage = (list as Array<{ languageId: string | number }>).reduce(
          (acc, translation) => {
            acc.set(String(translation.languageId), translation);
            return acc;
          },
          new Map(),
        );
        return { languages: ctx.read.languages(), translationsPerLanguage };
      },
      save: async (model, saveCtx) => {
        await outcomesApi.update(outcomeId(saveCtx.values), {
          translations: formatTranslationsForUpdate(
            model as Parameters<typeof formatTranslationsForUpdate>[0],
          ),
        });
      },
    },
  };

  const feedMappingTab: GridTab = {
    kind: "grid",
    id: "feed-mapping",
    label: "Feed mapping",
    createGrid: (getValues) =>
      buildFeedMappingGrid(
        () => outcomeId(getValues()),
        outcomesApi.getFeedTypeMappings.bind(outcomesApi),
      ),
    createSubmitGate: createFeedMappingSubmitGate,
    addRowLabel: "common.buttons.add_new",
    onAddRow: addFeedMappingRow,
    save: async (saveCtx: GridTabSaveContext) => {
      const gate = saveCtx.gate;
      if (!gate?.hasSuccessfulLoad() || !gate.isDirty()) return;
      if (!gate.validateForDialogSubmit()) throw new Error("Please fill the required fields");
      const fragment = gate.buildMergeFragment();
      if (!fragment) return;
      const entityId = outcomeId(saveCtx.values);
      await outcomesApi.update(entityId, fragment);
      if (!saveCtx.rows) throw new Error("Feed mapping grid is not available");
      saveCtx.rows.value = await fetchFeedTypeMappingsList(
        outcomesApi.getFeedTypeMappings,
        entityId,
        { isReadAfterWrite: true },
      );
      gate.markClean?.();
      notifySuccess();
    },
  };

  const prematchChildren = gameGroup("prematchGame");
  const liveChildren = gameGroup("liveGame");

  const edit: DialogConfig = {
    id: "edit-outcome",
    title: "Edit outcome",
    layout: { type: "popover" },
    api: {
      initialValues: async (row) => {
        const data = readApiData(await outcomesApi.getSingle(row.id));
        return (data && typeof data === "object" && !Array.isArray(data) ? data : {}) as Record<
          string,
          unknown
        >;
      },
      submit: async () => {},
    },
    structure: {
      type: "tabbed",
      savePerTab: true,
      tabs: [
        {
          id: "main",
          label: "Main",
          save: createInheritableTabSave({
            children: mainChildren,
            persist: async (patch, saveCtx: DialogTabSaveContext) => {
              await outcomesApi.update(outcomeId(saveCtx.values), patch);
              notifySuccess();
            },
          }),
          children: mainChildren,
        },
        {
          id: "prematch",
          label: "Prematch",
          save: createInheritableTabSave({
            children: prematchChildren,
            persist: async (patch, saveCtx) => {
              await outcomesApi.update(outcomeId(saveCtx.values), patch);
              notifySuccess();
            },
          }),
          children: prematchChildren,
        },
        {
          id: "live",
          label: "Live",
          save: createInheritableTabSave({
            children: liveChildren,
            persist: async (patch, saveCtx) => {
              await outcomesApi.update(outcomeId(saveCtx.values), patch);
              notifySuccess();
            },
          }),
          children: liveChildren,
        },
        translationsTab,
        feedMappingTab,
      ],
    },
  };

  const add: DialogConfig = {
    id: "add-outcome",
    title: "Add outcome",
    trigger: { type: "fab" },
    layout: { type: "popover" },
    structure: {
      type: "stepper",
      steps: [
        {
          id: "main",
          children: [
            {
              type: "group",
              name: "main",
              label: "Main",
              children: [
                {
                  type: "text-field",
                  name: "name",
                  label: "Name",
                  required: true,
                  placeholder: "Enter a name",
                },
                {
                  type: "text-field",
                  name: "eventMarketOutcomeName",
                  label: "Event market outcome name",
                  required: true,
                },
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: marketStatusOptions,
                  defaultValue: 1,
                },
                { type: "text-field", name: "info", label: "Info" },
                { type: "text-field", name: "position", label: "Position" },
              ],
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        const main = (data.main ?? {}) as Record<string, unknown>;
        return outcomesApi.create({
          marketId: getMarketId(),
          name: main.name,
          eventMarketOutcomeName: main.eventMarketOutcomeName,
          status: main.status,
          info: main.info,
          position: main.position,
        });
      },
    },
  };

  const remove: DialogConfig = {
    id: "delete-outcome",
    title: "Delete outcome",
    description: "Are you sure you want to delete this outcome?",
    layout: {
      type: "nxgds-dialog",
      actions: [
        { intent: "dismiss", label: "Cancel", variant: "secondary" },
        { intent: "submit", label: "Delete", variant: "danger" },
      ],
    },
    structure: { type: "flat", children: [] },
    api: {
      submit: async (_data, dialogCtx) => {
        try {
          await outcomesApi.delete(dialogCtx.row?.id);
          notify({
            variant: NXGDSStatusVariant.Positive,
            text: `You have successfully deleted "${dialogCtx.row?.name}" outcome.`,
          });
        } catch (error) {
          notifyApiError(error, `Unable to delete "${dialogCtx.row?.name}" outcome.`);
        }
      },
    },
  };

  return { add, edit, delete: remove };
}

export function createOutcomesTab(options: {
  ctx: BackofficeContext;
  outcomesApi: OutcomesApi;
}): GridTab {
  const { ctx, outcomesApi } = options;

  return {
    kind: "grid",
    id: "outcomes",
    label: "Outcomes",
    createGrid: (getValues): GridViewBlock => ({
      type: "grid",
      columns: outcomeColumns,
      syncRouteQuery: false,
      dialogs: createOutcomeDialogs({
        ctx,
        outcomesApi,
        getMarketId: () => getValues().id,
      }),
      api: {
        get: (filters) => outcomesApi.grid.get({ ...filters, marketId: getValues().id }),
        update: outcomesApi.grid.update,
        delete: outcomesApi.grid.delete,
      },
    }),
  };
}
